class PostRating {
  constructor(root) {
    this.root = root;
    this.stars = [];
    this.summary = null;
    this.thankYou = null;
    this.state = null;
    this.submitting = false;
    this.stateVersion = 0;
    this.hoverTimeouts = [];

    if (!this.root) {
      return;
    }

    this.init();
  }

  init() {
    const starsContainer = this.root.querySelector('.post-rating__stars');
    const stars = starsContainer
      ? Array.from(starsContainer.querySelectorAll('.post-rating__star'))
      : [];

    if (!starsContainer || stars.length === 0) {
      return;
    }

    const summary = this.root.querySelector('[data-rating-summary]');
    const thankYou = this.root.querySelector('[data-rating-thank-you]');
    const postId = parseInt(this.root.dataset.postId || '0', 10);
    const average = parseFloat(this.root.dataset.average || '0') || 0;
    const count = parseInt(this.root.dataset.count || '0', 10) || 0;

    if (!postId) {
      return;
    }

    this.stars = stars;
    this.starsContainer = starsContainer;
    this.summary = summary;
    this.thankYou = thankYou;
    this.state = {
      postId,
      average,
      count,
      hasUserRated: false,
    };

    const storageKey = `post-rating-${postId}`;
    const storage = window.localStorage || null;
    const alreadyRated = storage && storage.getItem(storageKey) === '1';

    if (alreadyRated) {
      this.state.hasUserRated = true;
      this.root.dataset.rated = 'true';
      this.showThankYou();
    }

    this.renderFromAverage();
    this.refreshRatingData();

    this.starsContainer.addEventListener('mouseleave', () => {
      if (!this.state) {
        return;
      }
      this.renderFromAverage();
    });

    this.stars.forEach((star) => {
      const value = parseInt(star.dataset.value || '0', 10);

      if (!value) {
        return;
      }

      star.addEventListener('mouseenter', () => {
        if (!this.state || this.state.hasUserRated) {
          return;
        }

        this.renderForValueAnimated(value);
      });

      star.addEventListener('click', () => {
        if (!this.state || this.state.hasUserRated || this.submitting) {
          return;
        }

        this.submit(value, storageKey);
      });
    });
  }

  renderFromAverage() {
    if (!this.state || this.stars.length === 0) {
      return;
    }

    this.clearHoverAnimation();

    const { average } = this.state;
    const fullStars = Math.floor(average);
    const partial = average - fullStars;

    this.stars.forEach((star, index) => {
      const starIndex = index + 1;
      let fill = 0;

      if (starIndex <= fullStars) {
        fill = 1;
      } else if (starIndex === fullStars + 1) {
        fill = Math.max(0, Math.min(1, partial));
      }

      this.setStarFill(star, fill);
      star.setAttribute('aria-checked', 'false');
    });
  }

  clearHoverAnimation() {
    if (!this.hoverTimeouts || this.hoverTimeouts.length === 0) {
      return;
    }

    this.hoverTimeouts.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });

    this.hoverTimeouts = [];
  }

  renderForValueImmediate(value) {
    this.stars.forEach((star, index) => {
      const starIndex = index + 1;
      const fill = starIndex <= value ? 1 : 0;

      this.setStarFill(star, fill);
      star.setAttribute('aria-checked', starIndex === value ? 'true' : 'false');
    });
  }

  renderForValueAnimated(value) {
    if (!this.state || this.stars.length === 0) {
      return;
    }

    this.clearHoverAnimation();

    this.stars.forEach((star) => {
      this.setStarFill(star, 0);
      star.setAttribute('aria-checked', 'false');
    });

    const stepMs = 60;

    this.stars.forEach((star, index) => {
      const starIndex = index + 1;

      if (starIndex > value) {
        return;
      }

      const timeoutId = window.setTimeout(() => {
        if (!this.state || this.state.hasUserRated) {
          return;
        }

        this.setStarFill(star, 1);
        star.setAttribute('aria-checked', starIndex === value ? 'true' : 'false');
      }, stepMs * starIndex);

      this.hoverTimeouts.push(timeoutId);
    });
  }

  setStarFill(star, fill) {
    const clamped = Math.max(0, Math.min(1, fill));
    const fillPercent = clamped * 100;
    star.setAttribute('data-fill', fillPercent.toFixed(2));
  }

  refreshRatingData() {
    if (!this.state) {
      return;
    }

    const requestVersion = this.stateVersion;
    const ajaxUrl = window.backendData?.ajaxUrl ?? '/wp-admin/admin-ajax.php';
    const body = new URLSearchParams();
    body.append('action', 'get_rating_data');
    body.append('postId', String(this.state.postId));

    fetch(ajaxUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body,
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data || !data.success || !data.data || !this.state) {
          return;
        }

        if (this.submitting || requestVersion !== this.stateVersion) {
          return;
        }

        this.state.count = Number(data.data.count) || 0;
        this.state.average = Number(data.data.average) || 0;

        this.renderFromAverage();
        this.updateSummary();
      })
      .catch(() => {});
  }

  getNonce(forceRefresh) {
    const cached = window.backendData?.ratingNonce;
    if (cached && !forceRefresh) {
      return Promise.resolve(cached);
    }

    const ajaxUrl = window.backendData?.ajaxUrl ?? '/wp-admin/admin-ajax.php';
    const body = new URLSearchParams();
    body.append('action', 'get_rating_nonce');

    return fetch(ajaxUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body,
    })
      .then((r) => r.json())
      .then((data) => {
        const nonce = data?.data?.nonce ?? '';
        if (nonce && window.backendData) {
          window.backendData.ratingNonce = nonce;
        }
        return nonce;
      });
  }

  submit(value, storageKey) {
    if (!this.state) {
      return;
    }

    const ajaxUrl = window.backendData?.ajaxUrl ?? '/wp-admin/admin-ajax.php';

    this.submitting = true;
    this.renderForValueImmediate(value);

    const trySubmit = (forceFreshNonce) => this.getNonce(forceFreshNonce).then((nonce) => {
      const body = new URLSearchParams();
      body.append('action', 'post_rating_submit');
      body.append('nonce', nonce);
      body.append('postId', String(this.state.postId));
      body.append('rating', String(value));

      return fetch(ajaxUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        body,
      }).then((response) => {
        if (response.status === 403 && !forceFreshNonce) {
          return trySubmit(true);
        }

        if (!response.ok) {
          throw new Error('Request failed');
        }

        return response.json();
      });
    });

    trySubmit(false)
      .then((data) => {
        if (!data || !data.success || !data.data) {
          throw new Error('Invalid response');
        }

        const nextCount = Number(data.data.rating_count) || 0;
        const nextAverage = Number(data.data.rating_average) || 0;

        this.state.count = nextCount;
        this.state.average = nextAverage;
        this.state.hasUserRated = true;
        this.stateVersion += 1;
        this.root.dataset.rated = 'true';

        this.updateSummary();
        this.showThankYou();

        if (storageKey && window.localStorage) {
          window.localStorage.setItem(storageKey, '1');
        }
      })
      .catch(() => {
        if (this.state) {
          this.renderFromAverage();
        }
      })
      .finally(() => {
        this.submitting = false;
      });
  }

  updateSummary() {
    if (!this.state || !this.summary) {
      return;
    }

    const { count, average } = this.state;

    if (count === 0) {
      this.summary.textContent = 'No ratings yet. Be the first to rate this article!';
      return;
    }

    const roundedAverage = Math.round(average * 10) / 10;

    this.summary.textContent = `${count} ratings, average ${roundedAverage} out of 5`;
  }

  showThankYou() {
    if (this.thankYou) {
      this.thankYou.hidden = false;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const ratingRoot = document.querySelector('.post-rating');

  if (!ratingRoot) {
    return;
  }

  new PostRating(ratingRoot);
});

